import { Link } from 'react-router-dom';

export default function Areas() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const areas = [
    {
      badge: 'Interpretação',
      title: 'Atores',
      subtitle: 'Interpretação Cênica',
      description: 'Para atores que buscam verdade emocional, foco interno e consistência interpretativa.',
      development: 'Desenvolvimento: construção comportamental, subtexto, ação perfeita, objetivos macro/micro, estado zero, disciplina emocional, densidade e leitura vibratória da cena.',
      techniques: [
        'Telepatia profissional',
        'Verdade emocional',
        'Presença estrutural',
        'Mapeamento energético',
        'Prontidão física',
        'Subtexto vivo',
        'Comunicação consciente'
      ],
      image: 'https://public.readdy.ai/ai/img_res/0735c75670726cee898e5b2e1e14203e.jpg',
      align: 'left',
      link: '/atores'
    },
    {
      badge: 'Liderança',
      title: 'Treinadores, Professores e Mestres',
      subtitle: 'Condução e Autoridade',
      description: 'Formação para quem conduz pessoas e precisa expandir liderança, energia, comunicação e leitura humana.',
      development: 'Resultados: autoridade vibratória clara, domínio emocional, foco, proteção energética, condução firme e equilibrada.',
      techniques: [
        'Telepatia aplicada',
        'Psicologia da condução',
        'Estado zero',
        'Ação perfeita',
        'Blindagem emocional',
        'Leitura comportamental',
        'Comunicação ativa'
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20coach%20trainer%20leading%20group%20of%20people%20in%20modern%20training%20facility%2C%20strong%20leadership%20presence%2C%20motivational%20atmosphere%2C%20dynamic%20energy%2C%20professional%20photography%2C%20warm%20lighting&width=800&height=600&seq=area-treinadores-001&orientation=landscape',
      align: 'right',
      link: '/treinadores'
    },
    {
      badge: 'Desenvolvimento',
      title: 'Estudantes e Jovens',
      subtitle: 'Formação Integral',
      description: 'Processo para estudantes a partir do 7º ano: foco, disciplina, organização interna, estabilização emocional e direção de carreira.',
      development: 'Trabalhos: verdade no sentir, blindagem emocional, ação perfeita, estado zero para estudo, resolução de conflitos e fortalecimento interno.',
      techniques: [
        'Foco e concentração',
        'Disciplina interna',
        'Organização mental',
        'Gestão emocional',
        'Autoconhecimento',
        'Direção de carreira',
        'Resolução de conflitos'
      ],
      image: 'https://readdy.ai/api/search-image?query=young%20students%20focused%20artistic%20practice%20and%20study%2C%20concentration%20discipline%2C%20modern%20bright%20educational%20environment%2C%20natural%20lighting%2C%20motivated%20youth%20learning%2C%20professional%20photography&width=800&height=600&seq=area-estudantes-001&orientation=landscape',
      align: 'left',
      link: '/estudantes'
    },
    {
      badge: 'Performance',
      title: 'Atletas Profissionais',
      subtitle: 'Alta Performance Esportiva',
      description: 'Para atletas, lutadores e competidores que precisam de foco, estabilidade mental, energia aplicada, força interna e alto desempenho.',
      development: 'Trabalhos: estabilidade emocional, ritmo interno, telepatia aplicada ao esporte, blindagem competitiva, foco decisório, regeneração energética, prevenção comportamental de lesões e cura de medos.',
      techniques: [
        'Estabilidade mental',
        'Foco competitivo',
        'Energia aplicada',
        'Blindagem emocional',
        'Ritmo interno',
        'Regeneração energética',
        'Superação de limites'
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20athlete%20intense%20training%20moment%20focused%20determination%20high%20performance%20sports%20facility%20dynamic%20movement%20powerful%20energy%20dramatic%20lighting%20dark%20background&width=800&height=600&seq=area-atletas-001&orientation=landscape',
      align: 'right',
      link: '/atletas'
    },
    {
      badge: 'Combate',
      title: 'Defesa & Ataque Pessoal',
      subtitle: 'Força e Proteção',
      description: 'Treinamento físico e energético para combate direto, proteção, autocontrole e força interna.',
      development: 'Bases: fato consciente, ataque objetivo, defesa física e vibratória, presença, superação de medos, fundamentos orientais (Bushidō) e unificação corpo–mente–energia.',
      techniques: [
        'Defesa física e energética',
        'Ataque consciente',
        'Autocontrole',
        'Presença de combate',
        'Fundamentos Bushidō',
        'Unificação corpo-mente',
        'Superação de medos'
      ],
      image: 'https://readdy.ai/api/search-image?query=martial%20arts%20training%20session%20focused%20discipline%20control%20traditional%20dojo%20atmosphere%20powerful%20stance%20professional%20martial%20arts%20photography%20dramatic%20lighting%20dark%20background&width=800&height=600&seq=area-combate-001&orientation=landscape',
      align: 'left',
      link: '/defesa-ataque'
    }
  ];

  return (
    <section id="areas" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#262364] mb-6">
            Áreas de Atuação
          </h2>
          <p className="text-base text-gray-700 max-w-3xl mx-auto">
            Programas especializados para cada perfil profissional, com metodologia exclusiva e resultados comprovados
          </p>
        </div>

        <div className="space-y-20">
          {areas.map((area, index) => (
            <div
              key={index}
              className={`flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center`}
            >
              {/* Image */}
              <div className={`w-full ${area.align === 'right' ? 'lg:order-2' : ''}`}>
                <div className="relative group overflow-hidden rounded-2xl lg:rounded-3xl">
                  <div className="w-full h-64 sm:h-80 lg:h-96">
                    <img
                      src={area.image}
                      alt={area.title}
                      className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-[#F05954] opacity-0 group-hover:opacity-15 transition-opacity duration-500"></div>
                </div>
              </div>

              {/* Content */}
              <div className={`w-full ${area.align === 'right' ? 'lg:order-1 lg:pr-12' : 'lg:pl-12'}`}>
                <div className="inline-block bg-[#F05954] text-white px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                  {area.badge}
                </div>
                
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#262364] mb-2">
                  {area.title}
                </h3>
                
                <p className="text-base text-[#F05954] font-semibold mb-4">
                  {area.subtitle}
                </p>
                
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  {area.description}
                </p>
                
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  {area.development}
                </p>
                
                <div className="mb-8">
                  <h4 className="text-xs font-bold text-[#262364] uppercase tracking-wider mb-3">
                    Técnicas Aplicadas:
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {area.techniques.map((technique, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#262364] flex-shrink-0 mt-2"></span>
                        <span className="text-sm text-gray-600">{technique}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {area.link ? (
                  <Link
                    to={area.link}
                    className="inline-block border-2 border-[#262364] text-[#262364] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#262364] hover:text-white transition-all whitespace-nowrap cursor-pointer"
                  >
                    Entrar na AAPEQ — {area.title}
                  </Link>
                ) : (
                  <button
                    onClick={() => scrollToSection('contato')}
                    className="border-2 border-[#262364] text-[#262364] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#262364] hover:text-white transition-all whitespace-nowrap cursor-pointer"
                  >
                    Entrar na AAPEQ — {area.title}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}